import { getAllQuestions } from "@/lib/utils/chart-utils";
import { findSurveyQuestion } from "@/features/studies/report/utils/findSurveyQuestion";
import {
  LiftQuestionMapEntry,
  LiftTaggedQuestion,
  readLiftTagsFromQuestion,
} from "@/features/studies/questionnaire/assets/liftTags";

interface QuestionMapEntry extends LiftQuestionMapEntry {
  questionId?: string;
  surveyJsName?: string;
}

/**
 * A lift-taggable question plus the two SurveyJS fields the title fallback
 * reads. `LiftTaggedQuestion` describes only what the tag reader needs; this
 * pairing also has to compare what the question ASKS and how it is answered.
 */
interface PairableQuestion extends LiftTaggedQuestion {
  title?: string;
  type?: string;
}

function resolveLiftPartner(
  question: PairableQuestion,
  questions: Array<PairableQuestion>,
  questionMap: Record<string, QuestionMapEntry> | null | undefined,
): PairableQuestion | null {
  const half = readLiftTagsFromQuestion(question, questionMap);
  if (!half) {
    return null;
  }

  const byMeasure = new Map<string, { pre?: PairableQuestion; post?: PairableQuestion }>();
  for (const candidate of questions) {
    const candidateHalf = readLiftTagsFromQuestion(candidate, questionMap);
    if (!candidateHalf) {
      continue;
    }
    const slot = byMeasure.get(candidateHalf.measure) ?? {};
    if (candidateHalf.half === "pre" && !slot.pre) {
      slot.pre = candidate;
    }
    if (candidateHalf.half === "post" && !slot.post) {
      slot.post = candidate;
    }
    byMeasure.set(candidateHalf.measure, slot);
  }

  const slot = byMeasure.get(half.measure);
  if (!slot?.pre || !slot?.post) {
    return null;
  }

  const questionName = question.name ?? "";
  if (questionName && questionName === slot.pre.name) {
    return slot.post;
  }
  if (questionName && questionName === slot.post.name) {
    return slot.pre;
  }
  return null;
}

function normalizeQuestionTitle(question: PairableQuestion | null | undefined): string {
  return String(question?.title ?? "").trim();
}

/**
 * True when any question in the survey carries a `lift:` tag (mirroring
 * the backend's `draftHasLiftTags` in
 * `apps/backend/src/questionnaires/common/runtime.ts`).
 *
 * A survey with a lift tag anywhere is provably post-migration: the builder
 * mints `lift:pre:`/`lift:post:` tags whenever it means two questions to
 * pair, so an untagged pair of same-titled questions elsewhere in that same
 * survey is a coincidence between two independently authored questions, not
 * a leftover untagged Before/After. Only a survey with NO lift tag anywhere
 * is legacy enough for the title fallback below to run.
 */
function surveyHasAnyLiftTag(
  questions: Array<PairableQuestion>,
  questionMap: Record<string, QuestionMapEntry> | null | undefined,
): boolean {
  return questions.some((candidate) => readLiftTagsFromQuestion(candidate, questionMap) !== null);
}

/**
 * Last resort, for a lift pair whose halves were never tagged: two questions
 * that ask the identical thing in the identical shape.
 *
 * Only EXACTLY one partner counts. Three questions sharing a title is a
 * question repeated per asset, not a pair, and pairing two of them would put a
 * second asset's answers beside the first as if they were a before and an
 * after. Types must match for the same reason: a rating and an open end that
 * happen to share a stem are not two halves of one measure.
 */
function resolveTitlePartner(
  question: PairableQuestion,
  questions: Array<PairableQuestion>,
): PairableQuestion | null {
  const title = normalizeQuestionTitle(question);
  if (!title) {
    return null;
  }

  const questionName = question.name ?? "";
  const partners: Array<PairableQuestion> = [];
  for (const candidate of questions) {
    if (!candidate.name || candidate.name === questionName) {
      continue;
    }
    if (normalizeQuestionTitle(candidate) !== title) {
      continue;
    }
    if (candidate.type !== question.type) {
      continue;
    }
    partners.push(candidate);
  }
  if (partners.length !== 1) {
    return null;
  }
  return partners[0];
}

/**
 * The question that belongs beside this one.
 *
 * `stampedPairedQuestionId` is analysis's own answer, resolved at build time
 * from the lift tags and carried on the payload. When the page has it, it wins:
 * one pairing decision, made once, rather than two surfaces guessing
 * separately and disagreeing.
 */
export function resolvePairedQuestion(
  survey: unknown,
  questionMap: Record<string, QuestionMapEntry> | null | undefined,
  questionId: string | null | undefined,
  stampedPairedQuestionId?: string | null,
): PairableQuestion | null {
  if (!questionId) {
    return null;
  }

  const questions = getAllQuestions(survey ?? {});
  const question = findSurveyQuestion<PairableQuestion>(survey, questionId, questionMap);
  if (!question) {
    return null;
  }

  if (stampedPairedQuestionId && stampedPairedQuestionId !== questionId) {
    const stamped = findSurveyQuestion<PairableQuestion>(
      survey,
      stampedPairedQuestionId,
      questionMap,
    );
    if (stamped) {
      return stamped;
    }
  }

  const liftPartner = resolveLiftPartner(question, questions, questionMap);
  if (liftPartner) {
    return liftPartner;
  }

  if (surveyHasAnyLiftTag(questions, questionMap)) {
    return null;
  }

  return resolveTitlePartner(question, questions);
}
